import { endOfMonth, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { InvoiceList } from '../../components/subscription/InvoiceList';
import { SubscriptionManager } from '../../components/subscription/SubscriptionManager';
import { UsageStats } from '../../components/subscription/UsageStats';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthForm } from '../../hooks/useAuthForm';
import { useSubscription } from '../../hooks/useSubscription';
import { supabase } from '../../lib/supabase';

interface UsageData {
  conversationCount: number;
  maxConversations: number;
}

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  created_at: string;
  invoice_url: string;
}

export default function ProfilesSettings() {
  const { user } = useAuth();
  const { loading, error, signOut } = useAuthForm();
  const { subscription, fetchSubscription } = useSubscription();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    if (user) {
      fetchSubscription(user.id);
    }
  }, [user, fetchSubscription]);

  useEffect(() => {
    if (user && subscription) {
      fetchUsageData();
      fetchInvoices();
    }
  }, [user, subscription]);

  const fetchUsageData = async () => {
    if (!user) return;

    try {
      // Récupérer le nombre de conversations du mois en cours
      const monthStart = format(new Date(), 'yyyy-MM-01');
      const { data: usageData } = await supabase
        .from('conversation_usage')
        .select('conversation_count')
        .eq('user_id', user.id)
        .eq('month_start', monthStart)
        .maybeSingle();

      // Récupérer la limite selon l'abonnement
      const { data: limitData } = await supabase
        .from('feature_limits')
        .select('max_limit')
        .eq('tier', subscription?.tier || 'free')
        .eq('feature', 'conversations_per_month')
        .maybeSingle();

      setUsage({
        conversationCount: usageData?.conversation_count || 0,
        maxConversations: limitData?.max_limit || 20
      });
    } catch (err) {
      console.error('Error fetching usage data:', err);
    }
  };

  const fetchInvoices = async () => {
    if (!user) return;
    
    // Skip fetching invoices if no stripe_customer_id is available or user is on free tier
    if (!subscription?.stripe_customer_id || subscription.tier === 'free') {
      setInvoices([]);
      return;
    }

    try {
      const { data: invoicesData } = await supabase.functions.invoke(
        "list-invoices",
        {
          body: { customerId: subscription.stripe_customer_id }
        }
      );

      if (invoicesData) {
        setInvoices(invoicesData);
      } else {
        setInvoices([]);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setInvoices([]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Profile and Security</h2>
        <p className="text-gray-400">Manage your profile and security settings</p>
      </div>

      {/* Informations du compte */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-6">Account information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <p className="text-white bg-white/5 rounded-lg px-3 py-2 border border-white/10">
              {user?.email}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subscription date
            </label>
            <p className="text-white bg-white/5 rounded-lg px-3 py-2 border border-white/10">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Abonnement */}
      <SubscriptionManager />

      {/* Consommation */}
      {usage && (
        <UsageStats 
          conversationCount={usage.conversationCount}
          maxConversations={usage.maxConversations}
          renewalDate={endOfMonth(new Date())}
        />
      )}

      {/* Factures */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-6">Invoice history</h3>
        <InvoiceList invoices={invoices} />
      </div>

      {/* Sécurité */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-6">Security</h3>
        <div className="space-y-4">
          <Link
            to="/app/settings/change-password"
            className="block w-full bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors text-center"
          >
            Change password
          </Link>

          <button
            onClick={signOut}
            disabled={loading}
            className="w-full bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            {loading ? 'Déconnexion...' : 'Se déconnecter'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
}